// src/pages/Premium.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import handleSubscription from "../stripe/handleSubscription";
import { useNavigate } from "react-router-dom";

export default function Premium() {
  const [loading, setLoading] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const auth = getAuth();
  const navigate = useNavigate();

  // Load premium status
  useEffect(() => {
    const fetchStatus = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setPremiumStatus(data.isPremium ? data.planType || "active" : null);
      }
    };
    fetchStatus();
  }, [auth]);

  const handleCheckout = async (plan) => {
    setLoading(true);
    try {
      const email = auth.currentUser?.email;
      if (!email) {
        alert("Please sign in first.");
        return;
      }
      await handleSubscription(plan, email);
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ReflectRise Premium – Unlock Your Full Potential</title>
      </Helmet>

      <div
        style={{
          background: "var(--bg)",
          color: "var(--text)",
          minHeight: "100vh",
          padding: "2rem 1rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            width: "100%",
            background: "var(--card)",
            borderRadius: "18px",
            padding: "2.5rem",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
          }}
        >
          {/* HEADER */}
          <h1
            style={{
              textAlign: "center",
              fontSize: "2.2rem",
              fontWeight: "700",
              background: "linear-gradient(135deg, var(--primary), var(--primary-2))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "1rem",
            }}
          >
            Upgrade to ReflectRise Premium
          </h1>

          <p
            style={{
              textAlign: "center",
              color: "var(--muted)",
              marginBottom: "2.5rem",
              fontSize: "1.1rem",
            }}
          >
            Compare plans and unlock advanced tools to elevate your growth.
          </p>

          {/* ---------------------------- */}
          {/*   SIDE-BY-SIDE COMPARISON    */}
          {/* ---------------------------- */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1.5rem",
            }}
          >
            {/* FREE PLAN CARD */}
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                padding: "1.8rem",
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
                Free Plan
              </h2>
              <p style={{ color: "var(--muted)", marginBottom: "1rem" }}>
                Start your journey with core tools.
              </p>

              <ul style={{ padding: 0, listStyle: "none", lineHeight: "1.9" }}>
                <li>✔ Unlimited Journal Entries</li>
                <li>✔ Unlimited Goals</li>
                <li>✔ Basic Mood Tracking</li>
                <li>✖ Dashboard</li>
                <li>✖ Streak Badges</li>
                <li>✖ Themes & Customisation</li>
                <li>✖ Daily Premium Quotes</li>
                <li>✖ Manage Subscription Page</li>
              </ul>

              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "0.8rem",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.05)",
                  textAlign: "center",
                }}
              >
                £0 / forever
              </div>
            </div>

            {/* PREMIUM PLAN CARD */}
            <div
              style={{
                background: "rgba(132,0,255,0.12)",
                padding: "1.8rem",
                borderRadius: "14px",
                border: "1px solid rgba(132,0,255,0.3)",
              }}
            >
              <h2
                style={{
                  fontSize: "1.5rem",
                  marginBottom: "0.5rem",
                  color: "var(--primary)",
                }}
              >
                Premium Plan
              </h2>
              <p style={{ color: "var(--muted)", marginBottom: "1rem" }}>
                Unlock all advanced features.
              </p>

              <ul style={{ padding: 0, listStyle: "none", lineHeight: "1.9" }}>
                <li>✔ Everything in Free Plan</li>
                <li>✔ Dashboard & Analytics</li>
                <li>✔ Streak Badges</li>
                <li>✔ Themes & Customisation</li>
                <li>✔ Daily Premium Quotes</li>
                <li>✔ Manage Subscription Page</li>
              </ul>

              {/* Already Premium? */}
              {premiumStatus ? (
                <button
                  onClick={() => navigate("/manage-subscription")}
                  style={{
                    marginTop: "1.5rem",
                    width: "100%",
                    background: "var(--primary)",
                    color: "#fff",
                    padding: "14px",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Manage Subscription
                </button>
              ) : (
                <>
                  {/* MONTHLY */}
                  <button
                    onClick={() => handleCheckout("monthly")}
                    disabled={loading}
                    style={{
                      marginTop: "1.5rem",
                      width: "100%",
                      background:
                        "linear-gradient(135deg, var(--primary), var(--primary-2))",
                      color: "#fff",
                      padding: "14px",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "1rem",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {loading ? "Processing..." : "£3.99 Monthly"}
                  </button>

                  {/* YEARLY */}
                  <button
                    onClick={() => handleCheckout("yearly")}
                    disabled={loading}
                    style={{
                      marginTop: "0.8rem",
                      width: "100%",
                      background: "linear-gradient(135deg, #E91E63, #9C27B0)",
                      color: "#fff",
                      padding: "14px",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "1rem",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {loading ? "Processing..." : "£29.99 Yearly"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
